import React, { useEffect, useState } from "react";
import { css } from "aphrodite";
import styles from "./styles";
import { useNavigate } from "react-router";
import { useSelector } from "react-redux";
import Appointment from "../../../Models/Appointment/Appointment";
import { RequestLayout } from "../../common/components/RequestLayout";
import { CustomDatePicker } from "../../common/components/CustomDatePicker";
import SlotListPicker from "../../common/components/SlotListPicker/SlotListPicker";
import {
    calculateEndTime,
    getNextStepPath, getSecondNextWorkingDate,
    isDateGreaterThan,
    isSameDate,
    stepLayoutItemsId,
    getAvailableDatesFromSlots,
    getFirstAvailableDateWithSlot,
} from "../../../Utils";
import { CommonLayout } from "../../common/components/CommonLayout";
import { StepInfo } from "../../common/components/StepInfo";
import CustomMainButton from "../../common/components/CustomMainButton/CustomMainButton";
import { RoutePath } from "../../../Utils/routePath";
import { Media } from "../../../Models";
import DateAndTimeMobileView from "./components/DateAndTimeMobileView/DateAndTimeMobileView";
import { Box } from "@mui/material";
import { useTranslation } from "react-i18next";

const DateAndTimePage = () => {
    const navigate = useNavigate();
    const currentStep = stepLayoutItemsId.selectDateAndTime;
    const stepItems = useSelector(Appointment.getStepItems);
    const serviceId = useSelector(Appointment.getSelectedServiceId);
    const selectedBranchId = useSelector(Appointment.getSelectedBranchId);
    const selectedSubServiceGroupId = useSelector(Appointment.getSelectedSubServiceGroupId);
    const availabilityData = useSelector(Appointment.getAvailabilityData);
    const selectedChannel = useSelector(Appointment.getSelectedChannel);
    const selectedEventTypeId = useSelector(Appointment.getSelectedEventTypeId);
    const loading = useSelector(Appointment.getAvailabilityLoading);
    const error = useSelector(Appointment.getAvailabilityError);
    const selectAvailableSlot = useSelector(
        Appointment.getSelectedAvailableSlot
    );
    const selectedSlotDataVisibility = useSelector(
        Appointment.getSelectedSlotDataVisibility
    );
    const isEditAppointmentMode = useSelector(
        Appointment.getIsEditAppointmentMode
    );
    const appointmentDataChanged = useSelector(
        Appointment.getAppointmentDataChanged
    );
    const appointmentEditData = useSelector(Appointment.getAppointmentEditData);
    const appointmentEditIsLoading = useSelector(
        Appointment.getAppointmentEditLoading
    );
    const appointmentEditError = useSelector(
        Appointment.getAppointmentEditError
    );
    const appointmentInfoData = useSelector(Appointment.getAppointmentInfoData);
    const selectedServiceName = useSelector(Appointment.getSelectedServiceName);
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [slotSelectedOnThisPage, setSlotSelectedOnThisPage] = useState(false);
    const webView = useSelector(Media.getIsWebView);
    const [t] = useTranslation(["translation"]);
    const currentDate = new Date();
    const isAppointmentByBranch = useSelector(Appointment.getIsAppointmentByBranch);
    const availableDatesFromSlots = availabilityData ? getAvailableDatesFromSlots(availabilityData.slots) : [];
    const firstAvailableDateWithSlot = availabilityData ? getFirstAvailableDateWithSlot(availableDatesFromSlots[0]) : new Date();
   
    useEffect(() => {
        if (isEditAppointmentMode) return; 

        if (!serviceId || !selectedBranchId) {
            if (isAppointmentByBranch) {
                if (!selectedBranchId) {
                    navigate(RoutePath.selectBranchPage, { replace: true });
                } else if (!serviceId) {
                    if (selectedSubServiceGroupId) {
                        navigate(RoutePath.selectSubServicePage, { replace: true });
                    } else {
                        navigate(RoutePath.selectServicePage, { replace: true });
                    }
                }
            } else {
                if (!serviceId) {
                    if (selectedSubServiceGroupId) {
                        navigate(RoutePath.selectSubServicePage, { replace: true });
                    } else {
                        navigate(RoutePath.selectServicePage, { replace: true });
                    }
                } else if (!selectedBranchId) {
                    navigate(RoutePath.selectBranchPage, { replace: true });
                }
            }
        }
    }, [serviceId, selectedBranchId, selectedSubServiceGroupId, isEditAppointmentMode, isAppointmentByBranch, navigate]);

    useEffect(() => {
        const startDay =getSecondNextWorkingDate()
        startDay.setHours(0, 0, 0, 0);
        startDay.setDate(startDay.getDate());        
        const endDay = new Date(startDay);
        endDay.setDate(endDay.getDate() + 30);
        if (serviceId && selectedBranchId) {
            const branch = selectedBranchId;
            Appointment.fetchAvailability(
                serviceId,
                branch,
                startDay.toISOString(),
                endDay.toISOString()
            );
        }
    }, [serviceId, selectedBranchId]);

    useEffect(() => {
        if (appointmentEditData?.id) {
            navigate(RoutePath.appointmentEditCompletedPage);
        }
    }, [appointmentEditData, navigate]);

    const handleContinue = () => {
        if (isEditAppointmentMode) {
            if (selectAvailableSlot) {
                Appointment.editAppointment(appointmentInfoData?.id, {
                    id: appointmentInfoData?.id,
                    branchId: selectedBranchId,
                    services: [serviceId],
                    start: selectAvailableSlot.start,
                    channelId: selectedChannel,
                    typeId: selectedEventTypeId,
                    title:
                        appointmentInfoData?.title ||
                        appointmentEditData?.name +
                            " for " +
                            selectedServiceName,
                    end:
                        selectAvailableSlot?.end ||
                        calculateEndTime(
                            selectAvailableSlot.start,
                            selectAvailableSlot.duration
                        ),
                    guests: appointmentInfoData?.guests || [],
                    users: appointmentInfoData?.users || [],
                });
            }
        } else {
            if (selectAvailableSlot) {
                Appointment.visibilityForSelectedSlotData(true);
                Appointment.stepCompleted(currentStep);
                navigate(getNextStepPath(currentStep, stepItems));
            }
        }
    };

    const handleOnDateChange = (newSelectedDate) => {
        setSelectedDate(newSelectedDate);
    };

    const handleSlotClick = (slot) => {
        // If clicking the same slot that's already selected, deselect it
        if (selectAvailableSlot?.start === slot.start) {
            Appointment.setSelectedAvailableSlot(null);
            setSlotSelectedOnThisPage(false);
        } else {
            // Otherwise, select the new slot
            Appointment.setSelectedAvailableSlot(slot);
            setSlotSelectedOnThisPage(true);
        }
    };

    return webView ? (
        <CommonLayout>
            <StepInfo activeStepId={currentStep} layoutItems={stepItems} />
            <RequestLayout
                error={error || appointmentEditError}
                loading={loading || appointmentEditIsLoading}
            >
                {availabilityData && (
                    <div className={css(styles.container)}>
                        <CustomDatePicker 
                            onDateChange={handleOnDateChange} 
                            title={t("Select Date")} 
                            minDate={firstAvailableDateWithSlot}
                            availableDates={availableDatesFromSlots}
                        />
                        <Box sx={{ width: "100%", maxWidth: 445 }}>
                            <SlotListPicker
                                title={t("Select Time")}
                                slotItems={availabilityData.slots.filter(
                                    (item) =>
                                        isSameDate(item.start, selectedDate) &&
                                        isDateGreaterThan(
                                            item.start,
                                            currentDate
                                        )
                                )}
                                onSlotClick={handleSlotClick}
                                activeSlot={selectAvailableSlot}
                            />
                            <CustomMainButton
                                isDisabled={!selectAvailableSlot || (!slotSelectedOnThisPage && !selectedSlotDataVisibility)}
                                value={t("Continue")}
                                fixedSize={false}
                                textVariant={"body1"}
                                style={styles.customButton}
                                onClick={handleContinue}
                            />
                        </Box>
                    </div>
                )}
            </RequestLayout>
        </CommonLayout>
    ) : (
        <DateAndTimeMobileView
            error={error || appointmentEditError}
            loading={loading || appointmentEditIsLoading}
            appointmentDataChanged={appointmentDataChanged}
            handleContinue={handleContinue}
            onSlotClick={handleSlotClick}
            selectAvailableSlot={selectAvailableSlot}
            slotSelectedOnThisPage={slotSelectedOnThisPage}
            selectedSlotDataVisibility={selectedSlotDataVisibility}
            slotItems={
                availabilityData
                    ? availabilityData?.slots?.filter(
                          (item) =>
                              isSameDate(item.start, selectedDate) &&
                              isDateGreaterThan(item.start, currentDate)
                      )
                    : []
            }
            onDateChange={handleOnDateChange}
            availableDates={availableDatesFromSlots}
            firstAvailableDateWithSlot={firstAvailableDateWithSlot}
        />
    );
};

export { DateAndTimePage };
